/**
 * CardNotesPopover — modal listing notes for a specific card + period.
 *
 * Supports adding, editing, and deleting notes.
 * Each note shows author name, timestamp, and content.
 */

import { useMemo, useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { MessageSquare, Pencil, Trash2, X, Check } from 'lucide-react'
import { useCardNotesStore, type CardNote } from '@/stores/card-notes-store'
import { useAuth } from '@/hooks/useAuth'

interface CardNotesPopoverProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  metric: string
  metricLabel: string
  periodKey: string
}

function formatPeriodLabel(periodKey: string): string {
  const [start, end] = periodKey.split('_')
  if (!start || !end) return periodKey
  const fmt = (iso: string) => {
    const d = new Date(iso + 'T00:00:00')
    return d.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' })
  }
  return `${fmt(start)} — ${fmt(end)}`
}

function NoteItem({
  note,
  currentUserId,
  onUpdate,
  onDelete,
}: {
  note: CardNote
  currentUserId: string
  onUpdate: (id: string, content: string) => void
  onDelete: (id: string) => void
}) {
  const [editing, setEditing] = useState(false)
  const [editText, setEditText] = useState(note.content)
  const isOwner = note.authorId === currentUserId

  const handleSave = () => {
    if (editText.trim()) {
      onUpdate(note.id, editText.trim())
      setEditing(false)
    }
  }

  const ts = new Date(note.updatedAt ?? note.createdAt)
  const timeLabel = ts.toLocaleDateString('pt-BR') + ' ' + ts.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })

  return (
    <div className="border border-brand-dark-lighter rounded-lg p-3">
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-[11px] font-semibold text-white truncate">
            {note.authorName}
          </span>
          <span className="text-[10px] text-brand-gray">
            {timeLabel}
          </span>
          {note.updatedAt && (
            <span className="text-[9px] text-brand-gray italic">(editado)</span>
          )}
        </div>
        {isOwner && !editing && (
          <div className="flex items-center gap-0.5 flex-shrink-0">
            <button
              type="button"
              onClick={() => { setEditText(note.content); setEditing(true) }}
              className="p-1 rounded text-brand-gray hover:text-white transition-colors"
              aria-label="Editar nota"
            >
              <Pencil size={12} />
            </button>
            <button
              type="button"
              onClick={() => onDelete(note.id)}
              className="p-1 rounded text-brand-gray hover:text-red-400 transition-colors"
              aria-label="Excluir nota"
            >
              <Trash2 size={12} />
            </button>
          </div>
        )}
      </div>

      {editing ? (
        <div className="space-y-2">
          <textarea
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            rows={2}
            className="w-full px-2 py-1.5 rounded bg-brand-dark border border-brand-dark-lighter text-white text-xs placeholder:text-brand-gray focus:border-brand-primary focus:outline-none resize-y"
            autoFocus
          />
          <div className="flex justify-end gap-1.5">
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="p-1 rounded text-brand-gray hover:text-white transition-colors"
            >
              <X size={14} />
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!editText.trim()}
              className="p-1 rounded text-brand-primary hover:text-brand-primary/80 transition-colors disabled:opacity-40"
            >
              <Check size={14} />
            </button>
          </div>
        </div>
      ) : (
        <p className="text-xs text-brand-gray-light leading-relaxed whitespace-pre-wrap">
          {note.content}
        </p>
      )}
    </div>
  )
}

export function CardNotesPopover({
  open,
  onOpenChange,
  metric,
  metricLabel,
  periodKey,
}: CardNotesPopoverProps) {
  const { user } = useAuth()
  const { addNote, updateNote, deleteNote } = useCardNotesStore()
  const allNotes = useCardNotesStore((s) => s.notes)
  const notes = useMemo(
    () => allNotes.filter((n) => n.metric === metric && n.periodKey === periodKey),
    [allNotes, metric, periodKey]
  )
  const [newNote, setNewNote] = useState('')

  const userId = user?.id ?? user?.email ?? 'unknown'
  const userName = user?.name ?? user?.email ?? 'Anônimo'

  const handleAdd = () => {
    if (!newNote.trim()) return
    addNote({
      metric,
      periodKey,
      authorId: userId,
      authorName: userName,
      content: newNote.trim(),
    })
    setNewNote('')
  }

  const handleDelete = (id: string) => {
    if (window.confirm('Excluir esta nota?')) {
      deleteNote(id)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg bg-brand-dark-light border-brand-dark-lighter text-white"
        overlayClassName="bg-black/80"
      >
        <DialogHeader>
          <DialogTitle className="text-white font-display text-lg flex items-center gap-2">
            <MessageSquare size={18} className="text-brand-primary" />
            Notas — {metricLabel}
          </DialogTitle>
          <DialogDescription className="text-brand-gray-light text-xs">
            Período: {formatPeriodLabel(periodKey)}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-2 max-h-[50vh] overflow-y-auto">
          {notes.length === 0 ? (
            <p className="text-xs text-brand-gray text-center py-6">
              Nenhuma nota para este card e período.
            </p>
          ) : (
            notes.map((note) => (
              <NoteItem
                key={note.id}
                note={note}
                currentUserId={userId}
                onUpdate={updateNote}
                onDelete={handleDelete}
              />
            ))
          )}
        </div>

        {/* Add new note */}
        <div className="mt-3 pt-3 border-t border-brand-dark-lighter">
          <textarea
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            placeholder="Adicionar uma nota..."
            rows={2}
            className="w-full px-3 py-2 rounded-lg bg-brand-dark border border-brand-dark-lighter text-white text-xs placeholder:text-brand-gray focus:border-brand-primary focus:outline-none resize-y"
          />
          <div className="flex justify-end mt-2">
            <button
              type="button"
              onClick={handleAdd}
              disabled={!newNote.trim()}
              className="px-3 py-1.5 rounded-lg text-xs font-medium bg-brand-primary text-brand-dark hover:bg-brand-primary/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Adicionar nota
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
